import React from 'react';
import type { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import Select from 'atom-components/form-fields-for-hook-form/select';
import Heading from 'atom-components/heading';
import TooltipItem from 'atom-components/tooltip-item';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import type { TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';
import { sanitizeText } from 'utils/parsing';
import type { MarkResponseFormValues } from './mark-response-form.schema';
import { markItemMarkerOrganisationUser, rubricItemTooltip } from './rubric-item.helpers';
import styles from './rubric-item.module.scss';

type Props = {
  rubricItem: TestRubricItemWithOptions;
  responseItemId: number;
  canEditMark: boolean;
  canAdministrateMark: boolean;
  defaultValues: MarkResponseFormValues;
  markItemMarkers: TestInstanceResponseMarkerWithCustomerUser[];
  markItemMarkerOrganisationUsers: BaseCustomerUser[];
};

export default function RubricItem({
  rubricItem,
  responseItemId,
  canEditMark,
  canAdministrateMark,
  defaultValues,
  markItemMarkers,
  markItemMarkerOrganisationUsers
}: Props): ReactElement {
  const { watch, control, setValue, formState } = useFormContext();
  const markItems = watch('markItems');
  const activeMarkerId = watch('activeMarkerId');

  // Todo: revisit this code when we have more time - make it more reusable.
  const markItem = markItems.find(
    (mi) => mi.rubricItemId === rubricItem.id && mi.responseItemId === responseItemId
  );
  const markItemIndex = markItems.findIndex(
    (mi) => mi.rubricItemId === rubricItem.id && mi.responseItemId === responseItemId
  );
  const savedMarkItem = defaultValues.markItems.find((smi) => smi.id === markItem?.id);

  const markerOrganisationUser = markItemMarkerOrganisationUser(
    markItemMarkers,
    markItemMarkerOrganisationUsers,
    savedMarkItem
  );
  const tooltip = rubricItemTooltip(
    canAdministrateMark,
    markerOrganisationUser,
    activeMarkerId,
    savedMarkItem
  );

  return (
    <div key={rubricItem.id}>
      <Heading level={5} className={styles.rubricItemHeading}>
        {rubricItem.displayName}
      </Heading>
      <TooltipItem tooltip={tooltip}>
        <Select
          id={rubricItem.id.toString()}
          ariaLabel="Select rubric item"
          className={styles.rubricItemSelect}
          control={control}
          name={`markItems.${markItemIndex}.submittedValue`}
          size={rubricItem.testRubricItemOptions.length + 1}
          disabled={!canEditMark}
          onChange={(event): void => {
            const selectedRubricItemOption = rubricItem.testRubricItemOptions.find(
              (o) => o.identifier === event.target.value
            );
            setValue(
              `markItems.${markItemIndex}`,
              {
                id: markItem?.id,
                submittedValue: selectedRubricItemOption?.identifier || '',
                rubricItemId: rubricItem.id,
                rubricItemOptionId: selectedRubricItemOption?.id,
                responseItemId,
                // Ensure that the active marker of the mark item is set only if
                // they are scoring the item for the first time, or have changed
                // the score from what the previous item marker selected.
                // Unmodified mark items should retain the previous item marker
                // so that we have a record of which marker scored the item.
                activeMarkerId:
                  selectedRubricItemOption?.identifier !== savedMarkItem?.submittedValue
                    ? activeMarkerId
                    : markItem?.activeMarkerId
              },
              {
                shouldValidate: true,
                shouldTouch: true
              }
            );
          }}
        >
          <Select.Option id={`${rubricItem.id}-no-score`} value="">
            - No Score -
          </Select.Option>
          {rubricItem.testRubricItemOptions.map((testRubricItemOption) => (
            <Select.Option
              key={testRubricItemOption.id}
              value={testRubricItemOption.identifier}
              id={testRubricItemOption.id.toString()}
            >
              {sanitizeText(testRubricItemOption.description)}
            </Select.Option>
          ))}
        </Select>
      </TooltipItem>
      {formState.errors.markItems && (
        <ErrorMessage
          errors={formState.errors}
          name={`markItems.${markItemIndex}.root`}
          className={styles.errorMessage}
        />
      )}
    </div>
  );
}
